import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useContractPdf, useSignContract, useUser } from '~/api/user';
import { Button, Dialog, PdfPreview, Spinner } from '~/components';
import { Checkbox, Field } from '~/components/Form';
import { statusColorThemes } from '~/styles/themes.css';
import { clsx } from '~/styles/utils';
import { toast } from '~/utils/toast';

import { bodyPdfStyle, errorStyle, formStyle } from './ContractDialog.css';

type Props = {
  onClose: () => void;
};

export const ContractDialog = ({ onClose }: Props) => {
  const { data: user } = useUser();
  const mutation = useSignContract();

  const userNeedsToSignAddendum = user.contractSigned && !user.addendumSigned;

  const { control, handleSubmit } = useForm<{ agreed: boolean }>({
    defaultValues: { agreed: false },
  });

  const { t } = useTranslation(['common', 'dashboard', 'settings']);

  const onSubmit = handleSubmit(() => {
    mutation.mutate(undefined, {
      onSuccess: () => onClose(),
      onError: () => toast.danger({ description: t('settings:accountCompletionModal.frameworkAgreement.error') }),
    });
  });

  const { data: contractPdf, isError } = useContractPdf(userNeedsToSignAddendum, !(user.contractSigned && user.addendumSigned));

  return (
    <Dialog.Body>
      <Dialog.Header
        title={
          userNeedsToSignAddendum
            ? t('dashboard:progression.steps.signAddendum2024')
            : t('dashboard:progression.steps.signContractModalTitle')
        }
      />

      {isError ? (
        <p className={clsx(errorStyle, statusColorThemes.danger)}>{t('common:error')}</p>
      ) : (
        <div className={bodyPdfStyle}>
          {contractPdf ? (
            <PdfPreview
              src={`data:application/pdf;base64,${contractPdf}`}
              title={t('dashboard:progression.steps.signContractModalTitle')}
            />
          ) : (
            <Spinner size={32} />
          )}
        </div>
      )}

      <form className={formStyle} onSubmit={onSubmit}>
        <Controller
          control={control}
          name="agreed"
          render={({ field: { name, onChange, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
            <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
              <Checkbox.Root checked={value} name={name} onCheckedChange={onChange}>
                <Checkbox.Label>{t('dashboard:frameworkAgreement.checkboxLabel')}</Checkbox.Label>
              </Checkbox.Root>

              <Field.Error match={!!error}>{error?.message}</Field.Error>
            </Field.Root>
          )}
          rules={{ required: { value: true, message: t('dashboard:frameworkAgreement.checkboxError') } }}
        />

        <Dialog.Actions>
          <Button icon="Send" loading={mutation.isPending} type="submit">
            {t('common:send')}
          </Button>
        </Dialog.Actions>
      </form>
    </Dialog.Body>
  );
};
